/* ---------------------------------------------------------------------------
   Entry point. Subscribes one render pass to the store and rebuilds the shell
   each time — which is cheap, because render() reconciles that tree against the
   nodes already on screen instead of replacing them (see core/dom.ts). Two
   things this pass still owns: how much of the DOM a change is allowed to touch
   at all (core/paintScope.ts), and putting focus back when a field IS replaced
   rather than patched — see preserveFocus below.
   --------------------------------------------------------------------------- */

import './styles/fonts.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/app.css';
/* After app.css so the scoped remap wins over the aliases it re-points, and
   before field.css so the field shell's own rules can build on MD3's values. */
import './styles/md3.css';
import './styles/field.css';
/* The box the modeller's Gantt is mounted into, and the translation of its
   colour vocabulary into this one. The view's own ~720 lines are NOT here:
   they ride with the lazy chunk — see src/screens/ganttBlock.ts. */
import './styles/gantt.css';
/* Bridge Rating's screens, which are mounted on this page — src/rating/mount.ts. */
import './styles/rating.css';
/* Last: the WinUI 3 look of the desk shell — the start page, the ribbon and the
   app page. Scoped to html[data-shell='desk'], set below, so the field shell and
   the printed sheets are untouched. */
import './styles/winui.css';

import { h, render } from './core/dom.ts';
import { store, actions, can, ganttShown, type State } from './core/store.ts';
import { onImageChange } from './components/imageSlot.ts';
import { titlebar } from './components/titlebar.ts';
import { navpane, visibleNav } from './components/navpane.ts';
import { ribbon } from './components/ribbon.ts';
import { statusbar } from './components/structurePane.ts';
import { onRatingAccepted, onRatingChange, onRatingSelect, ratingOpened, ratingScreen, ratingSlot, syncRating } from './rating/mount.ts';
import { TITLE as RATING_TITLE } from './rating/chrome.ts';
import { appForShell } from './core/apps.ts';
import { tabbar } from './components/tabbar.ts';
import { pagehead } from './components/pagehead.ts';
import { overviewScreen } from './screens/overview.ts';
import { bridgeDashboard } from './screens/bridgeDashboard.ts';
import { registerScreen } from './screens/register.ts';
import { networkScreen } from './screens/network.ts';
import { detailScreen, docksInRecord, recordWith } from './screens/detail.ts';
import { reportScreen } from './screens/report.ts';
import { summaryScreen } from './screens/summary.ts';
import { optionsScreen } from './screens/options.ts';
import { guidePane, guideScreen } from './screens/guide.ts';
import { peopleScreen } from './screens/people.ts';
import { refusesRegister, suspendedScreen } from './screens/suspended.ts';
import {
  passwordChangeScreen,
  showsPasswordChange,
  showsSignIn,
  signInScreen,
} from './screens/signin.ts';
import { setLocale } from './data/locale.ts';
import { defectPhotoCount } from './data/defects.ts';
import { syncMeshView } from './modeler/meshView.ts';
import { syncEntryView } from './modeler/entryView.ts';
import { syncModelerViewport, wireViewportMessages } from './modeler/viewport.ts';
import { restoreImportedModel } from './modeler/importModel.ts';
import { modelDeleteDialog } from './modeler/deleteDialog.ts';
import { modelImportDialog } from './modeler/importDialog.ts';
import { syncNepalMap } from './map/nepalMap.ts';
import { installFlyouts } from './components/flyout.ts';
import { syncMapScene, wireSceneStatus } from './map/scene3d.ts';
import { syncFlyMap, wireFlyStatus } from './map/flyMap.ts';
import { syncSiteMap } from './map/siteMap.ts';
import { syncGantt, wireGanttStatus } from './screens/ganttBlock.ts';
import { calendarOf } from './screens/programme.ts';
import { editDialog } from './screens/editDialog.ts';
import { inspectionDialog } from './screens/inspectionDialog.ts';
import { photoDialog } from './screens/photoDialog.ts';
import { stepProgressPhoto } from './screens/progressPhotos.ts';
import { stepWorksEntry, worksEntryDialog } from './screens/worksEntryDialog.ts';
import { inspectionSheetScreen } from './screens/inspectionSheet.ts';
import { measureDialog } from './screens/measureDialog.ts';
import { ratesDialog } from './screens/ratesDialog.ts';
import { treatmentsDialog } from './screens/treatmentsDialog.ts';
import { stateDialog } from './screens/stateDialog.ts';
import { baselineDialog, extensionDialog } from './screens/baselinePage.ts';
import { defectDialog } from './screens/defectDialog.ts';
import { progressDialog } from './screens/progressDialog.ts';
import { worksReportScreen } from './screens/worksReport.ts';
import { scheduleScreen } from './screens/schedule.ts';
import { siteScreen } from './screens/site.ts';
import {
  documentDialog,
  incidentDialog,
  siteReturnDialog,
} from './screens/siteDialogs.ts';
import { itemDialog, testDialog } from './screens/siteQuality.ts';
import { handoverDialog } from './screens/handover.ts';
import { importDialog } from './screens/importDialog.ts';
import { scopeFor } from './core/paintScope.ts';
import { startRouter } from './core/router.ts';
import { startServiceWorker, watchInstall } from './core/install.ts';
import { shellChrome } from './core/shell.ts';
import { allBridges, findBridge, heroModel } from './data/derive.ts';

const mount = document.getElementById('app');
if (!mount) throw new Error('#app not found');
const root: HTMLElement = mount;

/**
 * Where the dialogs render: their own container, a sibling of the render root.
 *
 * Every one of them is `position: fixed` over the whole viewport, so nothing in
 * the shell lays them out — and keeping them outside it means a keystroke in a
 * dialog does not rebuild the screen behind it. That rebuild was visible: a
 * titlebar, a nav pane and a screenful of panels thrown away and remade on every
 * character typed. `display: contents` so an empty layer cannot affect the page.
 */
const dialogLayer = h('div', { class: 'dlg-layer', style: 'display:contents' });
document.body.appendChild(dialogLayer);

/* --------------------------- screen dispatch --------------------------- */

function screen(s: Readonly<State>): HTMLElement {
  /* THE ONE ROLE THAT IS SHOWN NO REGISTER AT ALL. `suspended` does not carry
     `register.read`, so every /api read is refused — and without this line the
     application already had an answer for that reader, and the answer was the
     bundled seed drawn as if it were the register. See the header of
     src/screens/suspended.ts.

     Two screens are still reachable, and both on purpose: the guide, which is
     part of this build and needs no register to be useful, and Options, which is
     where signing out is. Everything else is the register.

     `can` is false only for a role the backend reported — never for a request
     that failed, and never on a device with no signal — so this cannot appear in
     front of an inspector in a gorge. */
  if (refusesRegister(s, s.screen)) return suspendedScreen(s);

  switch (s.screen) {
    case 'dashboard':
      return overviewScreen(s);
    case 'board':
      return bridgeDashboard(s);
    case 'list':
      return registerScreen(s);
    case 'network':
      return networkScreen(s);
    case 'detail':
      return detailScreen(s);
    case 'report':
      return reportScreen(s);
    case 'sheet':
      return inspectionSheetScreen(s);
    case 'summary':
      return summaryScreen(s);
    case 'works':
      return worksReportScreen(s);
    case 'schedule':
      return scheduleScreen(s);
    case 'site':
      return siteScreen(s);
    case 'options':
      return optionsScreen(s);
    case 'people':
      return peopleScreen(s);
    case 'guide':
      return guideScreen(s);
  }
}

/* --------------------------- focus preservation --------------------------- */

interface FocusSnapshot {
  id: string;
  start: number | null;
  end: number | null;
  scroll: number;
}

function captureFocus(): FocusSnapshot | null {
  const el = document.activeElement;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return null;
  if (!el.id) return null;
  return {
    id: el.id,
    start: el.selectionStart,
    end: el.selectionEnd,
    scroll: el.scrollTop,
  };
}

function restoreFocus(snap: FocusSnapshot | null): void {
  if (!snap) return;
  const el = document.getElementById(snap.id);
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  el.focus({ preventScroll: true });
  if (snap.start != null && snap.end != null) {
    try {
      el.setSelectionRange(snap.start, snap.end);
    } catch {
      /* some input types don't support selection ranges */
    }
  }
  el.scrollTop = snap.scroll;
}

/**
 * Which screen is on show, so a repaint can tell a change of screen from a
 * change within one.
 *
 * Scroll offsets used to have to be saved and put back around every repaint,
 * because the pane holding them was thrown away and rebuilt. They now survive on
 * their own — render() patches the pane in place — and what needs doing by hand
 * is the opposite: a NEW screen has to start at the top, in the content pane and
 * in the record's two independently scrolling columns both.
 */
let lastScreen: State['screen'] | null = null;

/* --------------------------- render --------------------------- */

/** The state currently on screen, for scopeFor above. */
let painted: Readonly<State> | null = null;

function paintDialogs(s: Readonly<State>): void {
  render(
    dialogLayer,
    modelDeleteDialog(s),
    modelImportDialog(s),
    editDialog(s),
    inspectionDialog(s),
    defectDialog(s),
    progressDialog(s),
    siteReturnDialog(s),
    incidentDialog(s),
    documentDialog(s),
    testDialog(s),
    itemDialog(s),
    handoverDialog(s),
    importDialog(s),
    worksEntryDialog(s),
    photoDialog(s),
    ratesDialog(s),
    treatmentsDialog(s),
    stateDialog(s),
    baselineDialog(s),
    extensionDialog(s),
    /* Above the Log Defect dialog it is opened from, and it does not close it —
       the draft underneath is what the measurement is written back into. */
    measureDialog(s),
  );
}

function paint(s: Readonly<State>): void {
  const scope = scopeFor(painted, s);
  painted = s;
  if (scope === 'nothing') return;

  document.documentElement.setAttribute('data-t', s.theme);
  document.documentElement.setAttribute('data-shell', s.shell);

  if (scope === 'dialogs') {
    const dialogFocus = captureFocus();
    paintDialogs(s);
    restoreFocus(dialogFocus);
    syncEntryView(s);
    /* The Log defect dialog carries the point it was opened at, and the record's
       3D view draws that as a marker while it is open — so opening and closing it
       is news to the canvas even though nothing else on the page moved. Cheap: a
       mounted view compares and returns. */
    syncMeshView(s);
    return;
  }

  const focus = captureFocus();
  const arriving = lastScreen !== s.screen;

  /* NO SESSION, NO SHELL. Drawn instead of everything rather than inside the
     content column: a title bar with a search box, a nav pane with eight
     destinations and a register count of "5 of 5" around a sign-in notice would
     be the same untruth in a smaller box, because not one of those controls can
     do anything until the session is back. The dialogs are painted all the same,
     so anything already open closes cleanly rather than being orphaned over a
     screen that no longer exists. See the header of src/screens/signin.ts. */
  if (showsSignIn(s) || showsPasswordChange(s)) {
    /* The order matters: a session that has gone beats a password that needs
       changing, because there is nothing to change it from. */
    const takeover = showsSignIn(s) ? signInScreen(s) : passwordChangeScreen(s);
    render(root, h('div', { class: ['shell', s.shell === 'field' && 'shell--field'] }, takeover));
    paintDialogs(s);
    lastScreen = s.screen;
    restoreFocus(focus);
    return;
  }
  /* Which chrome goes around the screen, decided in src/core/shell.ts rather
     than here. This file cannot be imported by a check — it is an entry point —
     so a rule written inline here is a rule nothing can verify. See shellChrome. */
  const app = appForShell(s.shell);
  const chrome = shellChrome(s.shell, app.id);
  /* Bridge Rating draws its own screens from its own store, into a slot this
     render leaves alone — see src/rating/mount.ts. Except the 3D model, which
     is this store's `detail` in every application. */
  const rating = app.separate === true && s.screen !== 'detail';
  /* Every application's screens open in the record's content panel, beside its
     explorer and property panel, rather than in place of the record — see
     docksInRecord. */
  const docked = !refusesRegister(s, s.screen) && docksInRecord(s, app.id);

  render(
    root,
    h(
      'div',
      {
        class: [
          'shell',
          s.shell === 'field' && 'shell--field',
          chrome.ribbon && 'shell--app',
          chrome.navpane && 'shell--start',
        ],
      },
      chrome.titlebar ? titlebar(s) : null,
      chrome.ribbon
        ? ribbon(s, !rating && (docked || s.screen === 'detail' || s.screen === 'schedule') ? findBridge(s.selectedId) : null)
        : null,
      h(
        'div',
        { class: 'body' },
        chrome.navpane ? navpane(s) : null,
        h(
          'div',
          { class: 'content' },
          // Every screen carries a header now, the record included: its actions
          // belong beside the structure's name, and repeating that name over a
          // panel below would say it twice. See components/pagehead.ts.
          // Except the Scheduler's programme, whose view is the whole page, and
          // the desk record: the ribbon carries its actions, and the structure's
          // name is already in the title bar's search and the status bar.
          rating || docked || s.screen === 'schedule' || (s.screen === 'detail' && s.shell !== 'field')
            ? null
            : pagehead(s),
          h(
            'main',
            {
              class: [
                'scroll',
                chrome.splitDetail && (s.screen === 'detail' || docked) && 'scroll--split',
                !docked && s.screen === 'schedule' && 'scroll--fill',
                !docked && rating && 'scroll--rating',
              ],
            },
            docked
              ? rating
                ? recordWith(s, ratingSlot(), {
                    title: RATING_TITLE[ratingScreen()],
                    key: 'rating',
                    document: ratingScreen() === 'method',
                  })
                : recordWith(s, screen(s))
              : rating
                ? ratingSlot()
                : screen(s),
          ),
        ),
        /* Help → Guide, docked on the right of whichever application is up until
           it is closed. Not on the field shell, which has no width to share — the
           guide is a screen there, reached from Options. */
        s.guideOpen && s.shell !== 'field' ? guidePane() : null,
      ),
      /* The app page's status bar: the connection, the department, the
         structure in hand. */
      chrome.ribbon ? statusbar(s) : null,
      /* Last in the tree and fixed to the foot of the viewport, so it paints over
         the scrolling content rather than being laid out beside it. */
      chrome.tabbar ? tabbar(s) : null,
    ),
  );

  paintDialogs(s);

  /* A screen you have just arrived at starts at the top. `.scroll` is
     `overflow: hidden` on the record (see .scroll--split in app.css) — the real
     scrolling there happens inside each `.panel__body` — so both have to be
     put back. */
  if (arriving) {
    const scroller = root.querySelector<HTMLElement>('.scroll');
    if (scroller) scroller.scrollTop = 0;
    /* Not the docked guide's: it is beside the screen rather than part of it, and
       a reader halfway down a task should not be sent back to the contents by
       pressing the control the task told them to. */
    root.querySelectorAll<HTMLElement>(':not(.guide-pane) > .panel__body').forEach((el) => {
      el.scrollTop = 0;
    });
  }

  lastScreen = s.screen;

  restoreFocus(focus);

  /* Bridge Rating, into the slot the render just left for it — or stood down.
     Told the structure in hand, so its register lights the one the explorer and
     the property panel are showing. */
  /* And while the bridge dashboard is up, whose rating tile is Bridge Rating's
     ratings (LC-30). */
  syncRating(s.selectedId, appForShell(s.shell).separate === true || s.screen === 'board');

  /* THE RECORD'S 3D MODEL, in the slot the render just left for it. Both of these
     own an element outside the render root — a canvas and an iframe — and both are
     reconciled after the render so they can measure the placeholder they sit over.

     TWO PATHS, AND ONLY ONE OF THEM IS THE FUTURE. `syncMeshView` renders the
     `.glb` the server derived from the CSiBridge export: the source's own triangles,
     one file, same on every device, nothing rebuilt. `syncModelerViewport` frames
     the vendored modeller, which reconstructs the bridge from its parameters, and it
     now runs only for a model that has no stored geometry to render — a model
     imported before the derivation existed, or from an export with no mesh in it.
     Each is a no-op while the other holds the slot; see the note at the top of
     src/modeler/meshView.ts for why the frame is the weaker of the two. */
  syncMeshView(s);
  syncEntryView(s);
  syncModelerViewport(s);

  /* AND THIS DEVICE FETCHES A MESH IT DOES NOT HAVE. An imported model's triangles
     live in the importing browser's IndexedDB and nowhere else, so the model came
     up here — a colleague's laptop, or this one on a different port, since
     IndexedDB is keyed by origin and a port is part of one — as a bridge with no
     substructure and nothing saying why. The export document is on the server as a
     matter of course now, and this replays it.

     Fire-and-forget from the paint, which is safe because every guard is inside:
     it does nothing for a parametric model, nothing when the server has no source,
     nothing when the mesh is already here, nothing while an import is running, and
     nothing at all after the first attempt at a given model. See
     restoreImportedModel — that list is the function.

     AND NOTHING AT ALL FOR A MODEL WITH STORED GEOMETRY, which is the guard that
     makes this vestigial. A device-local mesh exists to feed a framed modeller; a
     model whose `.glb` is in the bucket is rendered from that instead, so there is
     nothing on this device for the replay to repair. What remains is the older
     models, which have no derived geometry and still need the frame. */
  if (s.screen === 'detail') {
    const model = heroModel(s.models.models) ?? s.models.models[0];
    if (model) void restoreImportedModel(s.selectedId, model);
  }

  /* And the map, for the same reason the hero viewport is here: it has to measure
     the box it just drew into, and the box only exists once the render is done —
     see syncNepalMap. Unlike the two above it owns no element of its own; it only
     reports the size back, which the next paint draws its tile grid from. */
  syncNepalMap();
  /* The 3D view's canvas is the same kind of thing as the modeller's frames: a
     WebGL context that must outlive a repaint. It goes into the slot the render
     just made, and is torn down the moment the view is not wanted — a context
     nobody is looking at is one of sixteen a tab is allowed. `registerRevision`
     rides along because moving a structure changes the scene, and nothing else in
     state says the record's own geometry has changed. */
  /* `findBridge` is null only on an empty register, and a Site view with no
     structure to stand on has nothing to build a scene from — so it is torn down
     exactly as it is when the screen is not the network one. */
  const mapShown = s.screen === 'network' || (s.screen === 'dashboard' && s.homeView !== 'register');
  const sceneBridge = mapShown && s.mapMode === '3d' ? findBridge(s.selectedId) : null;
  if (sceneBridge !== null) {
    /* Which model's geometry to draw, if any is exported: the same one the record's
       hero shows, so the two views of a structure are of one model. `hasGeometry`
       comes off the index, so a build that has just finished changes this on the
       next refresh and the scene rebuilds with the real structure on it.

       `heroModel` rather than a rule of its own, because "the same one the record's
       hero shows" is a claim this line has to keep — it used to say `hasThumb` here
       and in the hero, and the day the hero's rule changed the two would have
       silently drawn different models of one structure. */
    const model = heroModel(s.models.models) ?? s.models.models[0];
    syncMapScene({
      bridge: sceneBridge,
      base: s.mapBase,
      revision: s.registerRevision,
      ...(model?.hasGeometry
        ? { hasGeometryFor: model.id, geometryVersion: model.geometryVersion }
        : {}),
    });
  } else {
    syncMapScene(null);
  }

  /* And the third map. Its own WebGL context and its own camera, so it is torn down
     the moment it is not wanted rather than parked — a browser gives a page a
     handful of contexts and this application can want four at once.
     `registerRevision` is what rebuilds its extruded decks after a structure has
     been placed by hand. */
  syncFlyMap(
    mapShown && s.mapMode === 'fly'
      ? {
          list: allBridges(),
          base: s.mapBase,
          selectedId: s.selectedId,
          revision: s.registerRevision,
        }
      : null,
  );

  /* The bridge dashboard's location map: one structure, its road, river and contours. */
  const boardBridge = s.screen === 'board' ? findBridge(s.selectedId) : null;
  syncSiteMap(boardBridge === null ? null : { bridge: boardBridge, theme: s.theme });

  /* AND THE PROGRAMME'S BARS, in the slot the Programme card or the report's
     section (h) has just left for it. The fourth thing on this page that owns
     its own subtree and is therefore reconciled after the render rather than
     by it — see the header of src/screens/ganttBlock.ts.

     ONE CONDITION FOR TWO SCREENS. The Gantt is offered on the record and on
     the construction report, both of which draw from `baseline`, and the slot
     either exists in the DOM or it does not — so this says when there is a
     plan the reader has asked to see, and `syncGantt` finds the slot or
     stands down. Naming the two screens here would be a third place to keep
     in step with what the cards decide.

     Editable in the Scheduler, for whoever manages the programme; frozen on
     the report. */
  const editGantt = s.app === 'scheduler' && s.baseline !== null && can(s, 'programme.manage');
  syncGantt(
    ganttShown(s) ? s.baseline : null,
    s.ganttTableWidth,
    editGantt && s.baseline ? calendarOf(s, s.baseline) : null,
  );
}

store.subscribe(paint);

/* Re-render when a photo slot gains or loses an image. */
onImageChange(() => paint(store.get()));

/* And when the 3D scene has something new to say. Its loading is asynchronous and
   outside the store — a WebGL scene is not state — so it reports progress the same
   way an image slot does: by asking for a repaint. */
wireSceneStatus(() => paint(store.get()));
/* And the fly-around map, which loads a megabyte of library before it can say
   anything at all. */
wireFlyStatus(() => paint(store.get()));
/* And Bridge Rating's own store, whose screen the ribbon's Rating tab shows as
   pressed. Its own screens repaint themselves; this redraws the frame round them. */
onRatingChange(() => {
  /* And the address, which names its screen and the rating open on it (LC-31). */
  actions.ratingMoved(ratingScreen(), ratingOpened());
  if (appForShell(store.get().shell).separate || store.get().screen === 'board') paint(store.get());
});
/* And a structure picked in Bridge Rating is the structure in hand everywhere:
   the explorer, the property panel and the 3D model follow it. */
onRatingSelect((id) => {
  if (id !== store.get().selectedId) actions.selectBridge(id);
});
onRatingAccepted(() => void actions.hydrate());
/* And the programme's Gantt, which fetches the modeller's schedule view the
   first time anybody on this session asks for the bars. */
wireGanttStatus(() => paint(store.get()));

/* --------------------------- keyboard shortcuts --------------------------- */

/* ALT+Q IS THE SEARCH BOX, as it is in Office — from anywhere, typing or not,
   because the point of the shortcut is to get out of whatever has focus. */
document.addEventListener('keydown', (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey || e.key.toLowerCase() !== 'q') return;
  const search = document.getElementById('header-search');
  if (!(search instanceof HTMLInputElement)) return;
  search.focus();
  search.select();
  e.preventDefault();
});

document.addEventListener('keydown', (e) => {
  const target = e.target as HTMLElement | null;
  const typing =
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement;
  if (typing || e.metaKey || e.ctrlKey || e.altKey) return;

  // And the delete confirmation, which is modal for a stronger reason than the
  // rest: Esc must cancel it rather than reach a shortcut behind it. The action
  // itself refuses while a delete is in flight — see closeModelDelete.
  if (store.get().modelDeleteBridgeId !== null) {
    if (e.key === 'Escape') {
      actions.closeModelDelete();
      e.preventDefault();
    }
    return;
  }

  // The import summary, which is modal in the same way and dismissed the same
  // way. Nothing is in flight behind it — the import is over by the time it
  // exists, and closing it changes nothing but whether it is on screen.
  if (store.get().modelImportResult !== null) {
    if (e.key === 'Escape') {
      actions.closeModelImportResult();
      e.preventDefault();
    }
    return;
  }

  // The defect's dialog over the Defects page (src/screens/detail.ts).
  if (store.get().defectFocusId !== null && document.querySelector('.dfd[role="dialog"]')) {
    if (e.key === 'Escape') {
      actions.showDefect(null);
      e.preventDefault();
    }
    return;
  }

  // Same for the edit dialog. Most of its own Esc presses land while focus is
  // inside one of its inputs and never reach here (see `typing` above) — this
  // only covers Esc while focus is on the dialog's own buttons.
  if (store.get().editingId !== null) {
    if (e.key === 'Escape') {
      actions.closeEdit();
      e.preventDefault();
    }
    return;
  }

  // And Edit state, whose Esc waits for an act in flight — see closeStateEdit.
  if (store.get().stateEditOpen) {
    if (e.key === 'Escape') {
      actions.closeStateEdit();
      e.preventDefault();
    }
    return;
  }

  // The baseline's two dialogs, which wait for a take in flight as their Cancel does.
  if (store.get().baselineForm !== null || store.get().eotDraft !== null) {
    if (e.key === 'Escape') {
      if (!store.get().baselineBusy) {
        if (store.get().eotDraft !== null) actions.cancelExtension();
        else actions.cancelBaselineForm();
      }
      e.preventDefault();
    }
    return;
  }

  // An enlarged defect photograph is modal too, and the cheapest of them to
  // dismiss — nothing is being edited behind it.
  if (store.get().photoDefectId !== null) {
    if (e.key === 'Escape') {
      actions.closePhoto();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      /* Between the defect's own photographs, where it has several. The count
         comes off the row the lightbox is drawing, as stepPhoto expects. */
      const s = store.get();
      const d = s.registerDefects.find(
        (r) => r.bridgeId === s.selectedId && r.modelDefectId === s.photoDefectId,
      );
      if (d) {
        actions.stepPhoto(e.key === 'ArrowRight' ? 1 : -1, defectPhotoCount(d));
        e.preventDefault();
      }
    }
    return;
  }

  // The same viewer, opened from an inspection rather than from a defect.
  if (store.get().inspectionPhotoId !== null) {
    if (e.key === 'Escape') {
      actions.closeInspectionPhoto();
      e.preventDefault();
    }
    return;
  }

  // And from Monitoring's progress photographs, which step with the arrow keys —
  // except where the photograph has focus, whose arrows pan it.
  if (store.get().progressPhotoId !== null) {
    if (e.key === 'Escape') {
      actions.closeProgressPhoto();
      e.preventDefault();
    } else if (
      (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
      !(e.target instanceof Element && e.target.closest('.photolb__stage'))
    ) {
      if (stepProgressPhoto(store.get(), e.key === 'ArrowLeft' ? -1 : 1)) e.preventDefault();
    }
    return;
  }

  // A works entry open from the calendar, which steps through the entries.
  if (store.get().worksEntryId !== null) {
    if (e.key === 'Escape') {
      actions.closeWorksEntry();
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      if (stepWorksEntry(store.get(), e.key === 'ArrowLeft' ? -1 : 1)) e.preventDefault();
    }
    return;
  }

  // The bulk import, which is modal for the strongest reason of any of these:
  // there may be a plan on screen that somebody is reading before approving it.
  if (store.get().importOpen) {
    if (e.key === 'Escape') {
      actions.closeImport();
      e.preventDefault();
    }
    return;
  }

  // And "Log defect", which every record offers whether or not it has a model.
  if (store.get().defectBridgeId !== null) {
    /* Its category list is a flyout, and Esc there shuts the list, not the
       dialog — installFlyouts handles it, and this handler runs first. */
    if (store.get().menu !== null) return;
    if (e.key === 'Escape') {
      actions.closeLogDefect();
      e.preventDefault();
    }
    return;
  }

  // And the "Add inspection" dialog, which the nav pane and the record header
  // both open — so it is now reachable from every screen, and a stray '2' must
  // not throw the register up behind it.
  if (store.get().addingInspectionId !== null) {
    if (e.key === 'Escape') {
      actions.closeAddInspection();
      e.preventDefault();
    }
    return;
  }

  /* THE PROGRAMME IN FULL VIEW, which is the one thing here that is not a
     dialog and is dismissed like one. It covers the title bar and the nav
     pane, so Esc is the way out that does not need the button found first —
     and it is LAST of these because everything above it is genuinely modal
     and is drawn over it. And unlike them it swallows only Esc: the bars
     filling the window are not a mode the rest of the keyboard has to be
     locked out of, so a reader who presses 2 for the register still gets the
     register. */
  if (store.get().ganttFull && e.key === 'Escape') {
    actions.toggleGanttFull();
    e.preventDefault();
    return;
  }

  /* THE NUMBER KEYS ARE THIS APP'S OWN LIST, in the order the pane draws it.
     They used to be ten cases naming ten screens, which was right while there
     was one application and is wrong now that there are four: `5` is the
     inventory sheet in Bridge Inspection and nothing at all in the Scheduler,
     and a hard-coded `actions.go('report')` there would have navigated a
     planner to a print document that is not in their pane.

     Bound from the SAME list the pane draws, so every key printed works and no
     key reaches a row that is not there — see the note on the numbering in
     `visibleNav`. Above nine the digits run out; no application has six
     visible screens. */
  const digit = Number(e.key);
  if (Number.isInteger(digit) && digit >= 1 && digit <= 9) {
    const entry = visibleNav(store.get())[digit - 1];
    if (!entry) return;
    actions.go(entry.screen);
    e.preventDefault();
    return;
  }

  switch (e.key) {
    case 'b':
      actions.toggleNav();
      break;
    default:
      return;
  }
  e.preventDefault();
});

/* --------------------------- boot --------------------------- */

wireViewportMessages();


/* The address bar first, so the screen and the structure a link names are in the
   store before anything is drawn. Painting first would show the overview for a
   frame and then replace it with the record that was actually asked for. */
/* The catalogue is a module variable, and hydrating the store from
   localStorage does not go through the action that keeps the two in step.
   Applied here, before anything is drawn, so a reload in Nepali does not
   paint once in English. */
setLocale(store.get().locale);

/* THE DEPARTMENT BEFORE EVERYTHING ELSE, because it decides what the interface
   is called, what its labels say, which bundled register the fallback is and
   which groupings the summary offers. From `?tenant=` and the build's own
   VITE_TENANT only — the authoritative answer comes from /api/health and arrives
   during hydrate, a second later. Resolved here so a single-department
   deployment never paints a frame of the wrong letterhead while it waits for the
   network, which is every offline boot. */
actions.tenantResolved();

/* `?shell=` before the router, because which shell is in force decides which
   screens an address is allowed to name — applyHash refuses the desk-only ones
   and has to know the answer before it reads the hash. Before the first paint for
   the same reason the locale is: opening `?shell=field` and seeing one frame of
   the desk shell is the flicker this ordering exists to prevent. */
actions.applyShellOverride(window.location.search);

/* The desk's menus close on Escape and on a press outside them. */
installFlyouts();

startRouter();

/* One synchronous notify rather than a direct paint() call: startRouter has just
   written to the store, which has a repaint queued for the next frame, and flush
   cancels it — otherwise boot paints the same state twice. */
store.flush();

/* Whether this device can be offered a home-screen install, and whether it is
   already running one. Before startServiceWorker rather than after, because the
   browser fires `beforeinstallprompt` once the worker is in place and a listener
   attached after that has missed it — the offer is a one-shot event, not a state
   anything can read back. Options is the only screen that draws the answer; see
   the second half of src/core/install.ts. */
watchInstall((state) => actions.installabilityChanged(state));

/* Offline support, last of everything at boot. It competes with the register
   fetch for the same connection and the app being usable beats it being
   installable a second earlier — see src/core/install.ts, which also carries the
   ?sw=off escape hatch. */
startServiceWorker();

/* What is waiting on this device, and a first attempt at sending it.
   Before hydrate rather than after: a queued inspection is already on the record
   optimistically, and pushing it first means the server's own copy is what the
   register read replaces it with. */
void actions.refreshQueue().then(() => actions.syncQueue());

/* And again whenever the connection comes back. `online` is the browser's own
   guess and it lies in both directions — a captive portal reports online, a VPN
   flap reports offline — so this is a prompt to try, not a promise it will work.
   syncQueue is safe to call when it will fail: every queued write is idempotent
   and a failed drain leaves the queue exactly as it was. */
window.addEventListener('online', () => void actions.syncQueue());

/* Fetch the register from D1. Paint first, so the shell is up immediately and the
   bundled seed is on screen while the request is in flight; the store's own
   subscription repaints when the real records land. */
void actions.hydrate();
